import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Checkbox, Chip, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TableSection } from './TableSection';
import type { TableColumn } from './TableSection';

export type AssignmentFilter = 'all' | 'assigned' | 'unassigned';

export interface AssignmentTableColumn<T> {
  id: string;
  label: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
}

export interface AssignmentTableProps<T> {
  title: ReactNode;
  /** Full candidate set for the current scope (already fetched); filtering/paging happens client-side
   * so the All/Assigned/Not Assigned toggle can reflect pending (unsaved) selection across pages. */
  items: readonly T[];
  getRowId: (row: T) => number;
  getSearchableText: (row: T) => string;
  columns: readonly AssignmentTableColumn<T>[];
  assignedIds: ReadonlySet<number>;
  originalAssignedIds?: ReadonlySet<number>;
  onToggle: (id: number) => void;
  onRowClick?: (row: T) => void;
  readOnly?: boolean;
  loading?: boolean;
  pageSize?: number;
  emptyMessage?: ReactNode;
  searchPlaceholder?: string;
}

export const AssignmentTable = <T,>({
  title,
  items,
  getRowId,
  getSearchableText,
  columns,
  assignedIds,
  originalAssignedIds = assignedIds,
  onToggle,
  onRowClick,
  readOnly = false,
  loading = false,
  pageSize = 10,
  emptyMessage,
  searchPlaceholder,
}: AssignmentTableProps<T>) => {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AssignmentFilter>('all');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((row) => {
      const isAssigned = assignedIds.has(getRowId(row));
      if (filter === 'assigned' && !isAssigned) return false;
      if (filter === 'unassigned' && isAssigned) return false;
      if (term && !getSearchableText(row).toLowerCase().includes(term)) return false;
      return true;
    });
  }, [items, assignedIds, filter, search, getRowId, getSearchableText]);

  const maxPages = Math.max(Math.ceil(filtered.length / pageSize), 1);
  const clampedPage = Math.min(page, maxPages - 1);
  const pageRows = filtered.slice(clampedPage * pageSize, (clampedPage + 1) * pageSize);

  const handleFilterChange = (value: AssignmentFilter) => {
    setFilter(value);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const tableColumns: TableColumn<T>[] = [
    {
      id: 'assigned',
      label: t('assigned', 'Assigned'),
      render: (row) => (
        <Checkbox
          checked={assignedIds.has(getRowId(row))}
          onClick={(event) => event.stopPropagation()}
          onChange={() => onToggle(getRowId(row))}
          disabled={readOnly}
          inputProps={{ 'aria-label': t('assigned', 'Assigned') }}
        />
      ),
    },
    {
      id: 'assignmentStatus',
      label: t('status', 'Status'),
      render: (row) => {
        const id = getRowId(row);
        const isAssigned = assignedIds.has(id);
        const wasAssigned = originalAssignedIds.has(id);
        const status = isAssigned && wasAssigned
          ? { label: t('assignmentStatusAssigned', 'Assigned'), color: 'success' as const }
          : isAssigned
            ? { label: t('assignmentStatusToBeAssigned', 'To Be Assigned'), color: 'info' as const }
            : wasAssigned
              ? { label: t('assignmentStatusToBeUnassigned', 'To Be Unassigned'), color: 'warning' as const }
              : { label: t('assignmentStatusAvailable', 'Available'), color: 'default' as const };

        return (
          <Chip
            label={status.label}
            color={status.color}
            size="small"
          />
        );
      },
    },
    ...columns.map((column) => ({
      id: column.id,
      label: column.label,
      align: column.align,
      render: (row: T) => column.render(row),
    })),
  ];

  return (
    <TableSection<T>
      title={title}
      columns={tableColumns}
      rows={pageRows}
      getRowId={(row) => getRowId(row)}
      onRowClick={onRowClick}
      recordCount={filtered.length}
      searchValue={search}
      searchPlaceholder={searchPlaceholder}
      onSearchChange={handleSearchChange}
      page={clampedPage}
      maxPages={maxPages}
      onPageChange={setPage}
      loading={loading}
      emptyMessage={emptyMessage}
      actions={(
        <Stack
          direction="row"
          spacing={1}
        >
          <Chip
            label={t('all', 'All')}
            color={filter === 'all' ? 'primary' : 'default'}
            onClick={() => handleFilterChange('all')}
            size="small"
          />
          <Chip
            label={t('assigned', 'Assigned')}
            color={filter === 'assigned' ? 'primary' : 'default'}
            onClick={() => handleFilterChange('assigned')}
            size="small"
          />
          <Chip
            label={t('notAssigned', 'Not Assigned')}
            color={filter === 'unassigned' ? 'primary' : 'default'}
            onClick={() => handleFilterChange('unassigned')}
            size="small"
          />
        </Stack>
      )}
    />
  );
};
